import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DashboardService } from '../../dashboard.service';
import { DirectieSeriiService } from 'src/app/platform/nomenclatoare/directie-serii/directie-serii.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { helper } from 'src/environments/helper';
import { NotificationService } from 'src/app/services/notification.service';
import Swal from 'sweetalert2';

interface Serie {
    id: number;
    name: string;
}

interface RegistruNumber {
    numar: string;
    data: string;
    exists: boolean;
}

@Component({
    selector: 'app-registru-valability-extended',
    templateUrl: './registru-valability-extended.component.html',
    styleUrls: ['./registru-valability-extended.component.scss']
})
export class RegistruValabilityExtendedComponent implements OnInit, OnDestroy {
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;

    successTitle: string = helper.dialogConfig.headers.success;
    successIcon: string = helper.dialogConfig.icons.success;
    successType: string = helper.dialogConfig.icons.success;
    helper = helper;

    isLoading = false;
    isLoadingSeries = false;
    isCheckingNumber = false;
    form!: FormGroup;

    series: Serie[] = [];
    existingNumber: RegistruNumber | null = null;
    numberOption: 'new' | 'existing' = 'new';

    private destroy$ = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            id_document: number,
            passedRequestId: number,
            valability: Date
        },
        private dialogRef: MatDialogRef<RegistruValabilityExtendedComponent>,
        private fb: FormBuilder,
        private notificationService: NotificationService,
        private docService: DashboardService,
        private directieSeriiService: DirectieSeriiService
    ) { }

    ngOnInit(): void {
        this.buildForm();
        this.loadSeries();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    buildForm(): void {
        this.form = this.fb.group({
            valability: [this.data.valability || null],  // Optional - no validator
            serieId: [null, Validators.required],
            numberOption: ['new', Validators.required]
        });

        // Watch for serie changes to check existing number
        this.form.get('serieId')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(serieId => {
                if (serieId) {
                    this.checkExistingNumber(serieId);
                } else {
                    this.existingNumber = null;
                }
            });
    }

    loadSeries(): void {
        this.isLoadingSeries = true;
        // Using PUT method as per existing implementation (activeSwitch)
        this.directieSeriiService.activeSwitch({})
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isLoadingSeries = false)
            )
            .subscribe({
                next: (response: any) => {
                    this.series = response.data || response || [];
                },
                error: (error) => {
                    console.error('Error loading series:', error);
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        'Eroare la incarcarea seriilor disponibile.',
                        this.errorIcon
                    );
                }
            });
    }

    checkExistingNumber(serieId: number): void {
        this.isCheckingNumber = true;
        this.existingNumber = null;

        this.docService.checkRegistruNumber(this.data.id_document, serieId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isCheckingNumber = false)
            )
            .subscribe({
                next: (response: any) => {
                    // Handle response - API returns data at root level or wrapped in data property
                    const data = response.data || response;
                    if (data?.exists) {
                        this.existingNumber = {
                            numar: data.number?.toString() || '',
                            data: data.date || '',
                            exists: true
                        };
                        // Auto-select existing option if number exists
                        this.form.patchValue({ numberOption: 'existing' });
                        this.numberOption = 'existing';
                    } else {
                        this.existingNumber = null;
                        this.form.patchValue({ numberOption: 'new' });
                        this.numberOption = 'new';
                    }
                },
                error: (error) => {
                    console.error('Error checking existing number:', error);
                }
            });
    }

    closeDialog(): void {
        this.dialogRef?.close({
            success: false,
        });
    }

    addMonthsToValability(months: number): void {
        const currentDate = new Date();
        let newDate = new Date(currentDate);

        newDate.setMonth(newDate.getMonth() + months);

        let day = currentDate.getDate();
        while (newDate.getDate() !== day) {
            newDate.setDate(newDate.getDate() - 1);
        }

        this.form.patchValue({
            valability: newDate
        });
    }

    clearValability(): void {
        this.form.patchValue({
            valability: null
        });
    }

    setNumberOption(option: 'new' | 'existing'): void {
        this.numberOption = option;
        this.form.patchValue({ numberOption: option });
    }

    submitRegistruExtended(): void {
        if (this.form.invalid) {
            return;
        }

        const formData = {
            valability: this.form.get('valability')?.value,
            serie_id: this.form.get('serieId')?.value,
            use_existing_number: this.numberOption === 'existing'
        };

        const selectedSerie = this.series.find(s => s.id === formData.serie_id);
        const serieLabel = selectedSerie ? selectedSerie.name : '';

        let confirmText = `Documentul va fi inregistrat in registru cu seria "${serieLabel}".`;
        if (this.numberOption === 'existing' && this.existingNumber) {
            confirmText += ` Va fi folosit numarul existent: ${this.existingNumber.numar}.`;
        } else {
            confirmText += ' Va fi generat un numar nou.';
        }
        confirmText += ' Continuati?';

        Swal.fire({
            title: this.helper.dialogConfig.commonTitles.areYouSure,
            text: confirmText,
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: this.helper.dialogConfig.buttonLabels.close,
            confirmButtonColor: this.helper.dialogConfig.colorButtons.standard,
            confirmButtonText: this.helper.dialogConfig.buttonLabels.confirm,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
            preConfirm: () => {
                return this.docService.registerExtended(this.data.id_document, formData)
                    .pipe(finalize(() => this.isLoading = false))
                    .toPromise();
            }
        }).then((result) => {
            if (result.isConfirmed && result.value) {
                const responseData = result.value.data || result.value;
                const numar = responseData?.numar || '';

                this.notificationService.warningSwal(
                    this.successTitle,
                    `Document inregistrat in registru cu succes! Numar: ${numar}`,
                    this.successIcon
                ).then(() => {
                    this.dialogRef?.close({
                        success: true,
                        numar: numar,
                        data: responseData
                    });
                });
            }
        }).catch((error) => {
            let msg;
            try {
                msg = error.error?.errors?.message?.[0] || error.error?.message || error.message;
            } catch (err) {
                msg = this.helper.dialogConfig.generalMessages.error;
            }
            this.notificationService.warningSwal(
                this.errorTitle,
                msg,
                this.errorIcon
            );
        });
    }
}
